/* ==========================================================================
   Buckaroo size finder
   --------------------------------------------------------------------------
   Matches a lumbar measurement to a Buckaroo size and the notch it lands on.

   Kept as a plain asset rather than inline in the Liquid snippet for two
   reasons: the finder can render twice on one page, and Liquid would evaluate
   any {{ }} it found in the source. Every string the merchant can edit is
   passed in through data- attributes, and placeholders use [size] / [notch]
   rather than braces so nothing here collides with Liquid.

   No dependencies. Self-initialising, and safe to call again after a Shopify
   section re-render.
   ========================================================================== */

(function () {
  'use strict';

  var MM_PER_INCH = 25.4;

  /* Sizes are looked up by their centre notch, so an odd number of notches
     has a true middle and an even number rounds to the lower of the two. */
  function centreIndex(notches) {
    return Math.floor((notches.length - 1) / 2);
  }

  function nearestNotchIndex(notches, mm) {
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < notches.length; i++) {
      var dist = Math.abs(mm - notches[i]);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  function fill(template, values) {
    var out = String(template || '');
    for (var key in values) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) continue;
      out = out.replace(new RegExp('\\[\\s*' + key + '\\s*\\]', 'gi'), values[key]);
    }
    return out;
  }

  function SizeFinder(root) {
    this.root = root;
    this.unit = root.getAttribute('data-bsf-unit') === 'in' ? 'in' : 'mm';
    /* Unit words are merchant-editable, and the belt drawing follows the chart:
       the tool belt has two rows of holes per notch, the casual belt one. */
    this.unitLabels = {
      mm: root.getAttribute('data-bsf-unit-mm') || 'mm',
      in: root.getAttribute('data-bsf-unit-in') || 'inches'
    };
    this.holeRows = root.getAttribute('data-bsf-rows') === '1' ? 1 : 2;
    /* hasAttribute rather than ||: an admin may deliberately clear a symbol */
    this.symbols = {
      mm: root.hasAttribute('data-bsf-mm-symbol') ? root.getAttribute('data-bsf-mm-symbol') : 'mm',
      in: root.hasAttribute('data-bsf-in-symbol') ? root.getAttribute('data-bsf-in-symbol') : '"'
    };

    this.input = root.querySelector('[data-bsf-input]');
    this.goBtn = root.querySelector('[data-bsf-go]');
    this.errorEl = root.querySelector('[data-bsf-error]');
    this.unitName = root.querySelector('[data-bsf-unitname]');
    this.resultEl = root.querySelector('[data-bsf-result]');
    this.sizeEl = root.querySelector('[data-bsf-size]');
    this.subEl = root.querySelector('[data-bsf-sub]');
    this.beltEl = root.querySelector('[data-bsf-belt]');
    this.notchesEl = root.querySelector('[data-bsf-notches]');
    this.altUpEl = root.querySelector('[data-bsf-alt-up]');
    this.altDownEl = root.querySelector('[data-bsf-alt-down]');
    this.tableRows = root.querySelectorAll('[data-bsf-row]');

    this.copy = {
      sub: root.getAttribute('data-bsf-sub-text') || '',
      altUp: root.getAttribute('data-bsf-alt-up-text') || '',
      altDown: root.getAttribute('data-bsf-alt-down-text') || '',
      tooSmall: root.getAttribute('data-bsf-too-small') || '',
      tooLarge: root.getAttribute('data-bsf-too-large') || '',
      invalid: root.getAttribute('data-bsf-invalid') || 'Enter your measurement as a number.',
      typo: root.getAttribute('data-bsf-typo') || '',
      phMm: root.getAttribute('data-bsf-ph-mm') || '',
      phIn: root.getAttribute('data-bsf-ph-in') || ''
    };

    this.rows = this.readData();
    if (!this.rows.length) return;

    this.min = this.rows[0].notches[0];
    this.max = this.rows[0].notches[this.rows[0].notches.length - 1];
    for (var i = 0; i < this.rows.length; i++) {
      var n = this.rows[i].notches;
      if (n[0] < this.min) this.min = n[0];
      if (n[n.length - 1] > this.max) this.max = n[n.length - 1];
    }

    this.lastMm = null;
    this.bind();
    this.syncUnitUI();
  }

  /* The chart is rendered into a JSON script tag by Liquid. Rows that are not
     fully numeric are dropped rather than rendered as NaN, so one bad line in
     the merchant's chart cannot take the whole tool down. */
  SizeFinder.prototype.readData = function () {
    var holder = this.root.querySelector('[data-bsf-data]');
    var parsed = [];
    if (!holder) return parsed;
    try {
      parsed = JSON.parse(holder.textContent) || [];
    } catch (e) {
      parsed = [];
    }
    var clean = [];
    for (var i = 0; i < parsed.length; i++) {
      var row = parsed[i];
      if (!row || !row.notches || row.notches.length < 2) continue;
      var ok = true;
      for (var j = 0; j < row.notches.length; j++) {
        if (typeof row.notches[j] !== 'number' || !isFinite(row.notches[j]) || row.notches[j] <= 0) {
          ok = false;
          break;
        }
      }
      if (ok) clean.push(row);
    }
    clean.sort(function (a, b) { return a.notches[0] - b.notches[0]; });
    return clean;
  };

  SizeFinder.prototype.bind = function () {
    var self = this;

    var unitBtns = this.root.querySelectorAll('[data-bsf-unit-btn]');
    Array.prototype.forEach.call(unitBtns, function (btn) {
      btn.addEventListener('click', function () {
        self.setUnit(btn.getAttribute('data-bsf-unit-btn'));
      });
    });

    if (this.goBtn) {
      this.goBtn.addEventListener('click', function () { self.submit(); });
    }
    if (this.input) {
      this.input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.keyCode === 13) {
          event.preventDefault();
          self.submit();
        }
      });
      this.input.addEventListener('input', function () {
        self.input.classList.remove('is-error');
        self.hideError();
      });
    }

    /* Offered when the number looks like centimetres or a dropped zero. */
    if (this.errorEl) {
      this.errorEl.addEventListener('click', function (event) {
        var fixBtn = event.target.closest('[data-bsf-fix]');
        if (!fixBtn) return;
        self.input.value = fixBtn.getAttribute('data-bsf-fix');
        self.submit();
      });
    }
  };

  SizeFinder.prototype.setUnit = function (next) {
    if (next !== 'mm' && next !== 'in') return;
    if (next === this.unit) return;

    /* Carry whatever is already typed across rather than clearing it — someone
       who typed 920 and then realises they wanted inches means 920mm. */
    var typed = parseFloat(this.input && this.input.value);
    if (!isNaN(typed)) {
      var mm = this.unit === 'in' ? typed * MM_PER_INCH : typed;
      this.input.value = next === 'in' ? (mm / MM_PER_INCH).toFixed(1) : String(Math.round(mm));
    }

    this.unit = next;
    this.syncUnitUI();
    if (this.lastMm !== null) this.show(this.lastMm);
  };

  SizeFinder.prototype.syncUnitUI = function () {
    var self = this;
    var btns = this.root.querySelectorAll('[data-bsf-unit-btn]');
    Array.prototype.forEach.call(btns, function (btn) {
      var on = btn.getAttribute('data-bsf-unit-btn') === self.unit;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (this.unitName) this.unitName.textContent = this.unitLabels[this.unit];
    if (this.input) {
      this.input.setAttribute('placeholder', this.unit === 'in' ? this.copy.phIn : this.copy.phMm);
    }
  };

  SizeFinder.prototype.format = function (mm) {
    if (this.unit === 'in') return (mm / MM_PER_INCH).toFixed(1) + this.symbols.in;
    return Math.round(mm) + this.symbols.mm;
  };

  SizeFinder.prototype.showError = function (html) {
    if (!this.errorEl) return;
    this.errorEl.innerHTML = html;
    this.errorEl.hidden = false;
    if (this.input) this.input.classList.add('is-error');
    if (this.resultEl) this.resultEl.hidden = true;
    this.lastMm = null;
  };

  SizeFinder.prototype.hideError = function () {
    if (this.errorEl) this.errorEl.hidden = true;
  };

  SizeFinder.prototype.submit = function () {
    if (!this.input) return;
    var raw = String(this.input.value).replace(/[^0-9.]/g, '');
    var typed = parseFloat(raw);

    if (isNaN(typed) || typed <= 0) {
      this.showError(this.escape(this.copy.invalid));
      return;
    }

    var mm = this.unit === 'in' ? typed * MM_PER_INCH : typed;

    if (mm < this.min || mm > this.max) {
      /* A dropped zero or centimetres instead of millimetres are the two
         mistakes people actually make, so offer the correction inline. */
      var suggestion = null;
      if (mm * 10 >= this.min && mm * 10 <= this.max) suggestion = mm * 10;
      else if (mm / 10 >= this.min && mm / 10 <= this.max) suggestion = mm / 10;

      if (suggestion !== null && this.copy.typo) {
        var shown = this.unit === 'in'
          ? (suggestion / MM_PER_INCH).toFixed(1)
          : String(Math.round(suggestion));
        this.showError(
          this.escape(fill(this.copy.typo, { value: this.format(mm) })) +
          ' <button type="button" data-bsf-fix="' + this.escape(shown) + '">' +
          this.escape(this.format(suggestion)) + '</button>'
        );
        return;
      }

      var msg = mm < this.min
        ? fill(this.copy.tooSmall, { min: this.format(this.min), value: this.format(mm) })
        : fill(this.copy.tooLarge, { max: this.format(this.max), value: this.format(mm) });
      this.showError(this.escape(msg));
      return;
    }

    this.hideError();
    if (this.input) this.input.classList.remove('is-error');
    this.show(mm);
  };

  SizeFinder.prototype.escape = function (str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  /* Every size whose notch range covers the measurement is a genuine fit; the
     recommendation is whichever one puts the measurement closest to its centre
     notch, which is where a belt has room to move in both directions. */
  SizeFinder.prototype.fits = function (mm) {
    var out = [];
    for (var i = 0; i < this.rows.length; i++) {
      var n = this.rows[i].notches;
      if (mm >= n[0] && mm <= n[n.length - 1]) out.push(this.rows[i]);
    }
    out.sort(function (a, b) {
      return Math.abs(mm - a.notches[centreIndex(a.notches)]) -
             Math.abs(mm - b.notches[centreIndex(b.notches)]);
    });
    return out;
  };

  SizeFinder.prototype.show = function (mm) {
    this.lastMm = mm;

    var fits = this.fits(mm);
    if (!fits.length) return;

    var best = fits[0];
    var hit = nearestNotchIndex(best.notches, mm);

    if (this.sizeEl) this.sizeEl.textContent = best.size;
    if (this.subEl) {
      this.subEl.textContent = fill(this.copy.sub, {
        notch: hit + 1,
        total: best.notches.length,
        size: best.size,
        value: this.format(mm)
      });
    }

    this.renderBelt(best, hit);
    this.renderNotches(best, hit);
    this.renderAlternates(mm, fits, best, hit);
    this.renderTable(best, hit);

    if (this.resultEl) {
      this.resultEl.hidden = false;
      /* Announced rather than scrolled to: yanking the page is hostile when
         the result is already in view on desktop. */
      this.resultEl.setAttribute('aria-live', 'polite');
    }
  };

  SizeFinder.prototype.renderBelt = function (row, hit) {
    if (!this.beltEl) return;
    var n = row.notches.length;
    var firstX = 64;
    var lastX = 286;
    var step = n > 1 ? (lastX - firstX) / (n - 1) : 0;

    var svg = '<svg viewBox="0 0 364 80" xmlns="http://www.w3.org/2000/svg" role="img" ' +
              'aria-label="Belt diagram showing notch ' + (hit + 1) + ' of ' + n + '">';
    svg += '<path d="M10 17 H296 L352 40 L296 63 H10 A7 7 0 0 1 3 56 V24 A7 7 0 0 1 10 17 Z" ' +
           'fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>';

    for (var i = 0; i < n; i++) {
      var x = (firstX + step * i).toFixed(1);
      var on = i === hit;
      var stroke = on ? 'var(--bsf-orange)' : 'currentColor';
      var fillCol = on ? 'var(--bsf-orange)' : 'none';
      var r = on ? 4.6 : 3.2;
      if (this.holeRows === 1) {
        /* Casual/uniform belt: one hole per notch, on the centre line */
        svg += '<circle cx="' + x + '" cy="40" r="' + (on ? 5 : 3.6) + '" fill="' + fillCol + '" stroke="' + stroke + '" stroke-width="1.8"/>';
        if (on) {
          svg += '<ellipse cx="' + x + '" cy="40" rx="12" ry="15" fill="none" stroke="var(--bsf-orange)" stroke-width="1.7"/>';
        }
      } else {
        svg += '<circle cx="' + x + '" cy="31" r="' + r + '" fill="' + fillCol + '" stroke="' + stroke + '" stroke-width="1.8"/>';
        svg += '<circle cx="' + x + '" cy="49" r="' + r + '" fill="' + fillCol + '" stroke="' + stroke + '" stroke-width="1.8"/>';
        if (on) {
          svg += '<ellipse cx="' + x + '" cy="40" rx="12" ry="21" fill="none" stroke="var(--bsf-orange)" stroke-width="1.7"/>';
        }
      }
    }
    svg += '</svg>';
    this.beltEl.innerHTML = svg;
  };

  SizeFinder.prototype.renderNotches = function (row, hit) {
    if (!this.notchesEl) return;
    var html = '';
    for (var i = 0; i < row.notches.length; i++) {
      html += '<div class="bsf-notch' + (i === hit ? ' is-on' : '') + '">' +
              '<span class="bsf-notch__n">' + (i + 1) + '</span>' +
              '<span class="bsf-notch__v">' + this.escape(this.format(row.notches[i])) + '</span>' +
              '</div>';
    }
    this.notchesEl.innerHTML = html;
  };

  /* A size one step up that puts the measurement on an earlier notch has room
     to let out for layers; one step down on a later notch pulls in tighter.
     Only offered when they genuinely exist in the chart. */
  SizeFinder.prototype.renderAlternates = function (mm, fits, best, hit) {
    var up = null;
    var down = null;
    for (var i = 1; i < fits.length; i++) {
      var row = fits[i];
      var idx = nearestNotchIndex(row.notches, mm);
      var bigger = parseFloat(row.size) > parseFloat(best.size);
      if (!up && bigger && idx < hit) up = { row: row, idx: idx };
      if (!down && !bigger && idx > hit) down = { row: row, idx: idx };
    }

    this.setAlt(this.altUpEl, this.copy.altUp, up, hit);
    this.setAlt(this.altDownEl, this.copy.altDown, down, hit);
  };

  SizeFinder.prototype.setAlt = function (el, template, alt) {
    if (!el) return;
    if (!alt || !template) {
      el.hidden = true;
      return;
    }
    var text = fill(template, { size: alt.row.size, notch: alt.idx + 1 });
    /* Bolds the size wherever the merchant put it in their own wording. */
    el.innerHTML = this.escape(text).replace(
      new RegExp('(' + this.escape(String(alt.row.size)).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')'),
      '<strong>$1</strong>'
    );
    el.hidden = false;
  };

  SizeFinder.prototype.renderTable = function (row, hit) {
    Array.prototype.forEach.call(this.tableRows, function (tr) {
      var on = tr.getAttribute('data-bsf-row') === String(row.size);
      tr.classList.toggle('is-on', on);
      var cells = tr.querySelectorAll('[data-bsf-cell]');
      Array.prototype.forEach.call(cells, function (td, i) {
        td.classList.toggle('is-hit', on && i === hit);
      });
    });
  };

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-bsf-root]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.bsfReady === '1') return;
      root.dataset.bsfReady = '1';
      new SizeFinder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Shopify replaces a section's markup wholesale when its settings change in
     the theme editor, which drops the old listeners with it. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  window.BuckarooSizeFinder = { init: initAll };
})();
